class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.currentVariant = null;
  }

  connectedCallback() {
    this.variantGalleries = Array.from(
      this.querySelectorAll("site-carousel[data-variant-images]")
    );
    this.addEventListener("variant:change", (e) =>
      this.applyVariant(e.detail.variant)
    );
    this.bindHoverEvents();
    this.initThumbScrolling();
    this.handleAutoAction();
  }

  bindHoverEvents() {
    const currentVariantValues = [];
    this.querySelectorAll(".product__variants [data-variant-title]").forEach((variantLink) => {
      const parent = variantLink.closest('.product__variants')
      const titleHolder = parent?.querySelector('[variant-title-holder]')
      
      variantLink.addEventListener("mouseenter", () => {
        titleHolder.innerText = variantLink.dataset.variantTitle
      })
      variantLink.addEventListener("mouseleave", () => {
        titleHolder.innerText = titleHolder.getAttribute('variant-title-holder')
      })
    })
  }

  initThumbScrolling() {
    const carousels = Array.from(this.querySelectorAll("site-carousel"));
    if (!carousels.length) return;

    const missingSwiper = carousels.some((c) => !c.swiper);
    if (missingSwiper) {
      requestAnimationFrame(() => this.initThumbScrolling());
      return;
    }

    carousels.forEach((carousel) => {
      const swiper = carousel.swiper;
      if (!swiper) return;

      const thumbTrack = carousel.querySelector(".thumb-track");
      const thumbs = Array.from(
        carousel.querySelectorAll(".site-carousel-thumb")
      );
      if (!thumbTrack || !thumbs.length) return;

      const firstThumb = thumbs[0];
      const style = window.getComputedStyle(firstThumb);
      const thumbHeight = firstThumb.offsetHeight;
      const marginBottom = parseFloat(style.marginBottom || "0");
      const step = thumbHeight + marginBottom;

      carousel._thumbTrack = thumbTrack;
      carousel._thumbs = thumbs;
      carousel._thumbStep = step;

      const update = (activeIndex) => {
        this.scrollThumbsForCarousel(carousel, activeIndex);
        this.setActiveThumbOpacity(carousel, activeIndex);
      };

      this.initVideoThumbModals(carousel);

      const initialIndex =
        typeof swiper.realIndex === "number"
          ? swiper.realIndex
          : swiper.activeIndex || 0;
      update(initialIndex);

      swiper.on("slideChange", () => {
        const idx =
          typeof swiper.realIndex === "number"
            ? swiper.realIndex
            : swiper.activeIndex || 0;
        update(idx);
      });
    });
  }

  scrollThumbsForCarousel(carousel, activeIndex) {
    const thumbTrack = carousel._thumbTrack;
    const thumbs = carousel._thumbs;
    const step = carousel._thumbStep;

    if (!thumbTrack || !thumbs || thumbs.length <= 4 || !step) return;

    const visibleCount = 4;
    const maxStartIndex = thumbs.length - visibleCount;

    const startIndex = Math.max(
      0,
      Math.min(activeIndex - (visibleCount - 1), maxStartIndex)
    );

    const offset = -startIndex * step;

    thumbTrack.style.transform = `translateY(${offset}px)`;
  }

  setActiveThumbOpacity(carousel, activeIndex) {
    const thumbs = carousel._thumbs;
    if (!thumbs) return;

    thumbs.forEach((thumb, i) => {
      if (i === activeIndex) {
        thumb.classList.remove("opacity-50");
        thumb.classList.add("opacity-100");
      } else {
        thumb.classList.add("opacity-50");
        thumb.classList.remove("opacity-100");
      }
    });
  }

  initVideoThumbModals(carousel) {
    const thumbs = carousel._thumbs;
    const slides = carousel.querySelectorAll(".swiper-slide");
    if (!thumbs || !slides.length) return;

    thumbs.forEach((thumb, i) => {
      const videoId = thumb.dataset.videoId || slides[i]?.dataset?.videoId;
      if (!videoId) return;

      thumb.addEventListener("click", () => {
        // Click the play button to trigger video-modal.js + lockup injection
        const playBtn = document.querySelector(
          `.video-modal__play[data-id="${videoId}"]`
        );
        if (playBtn) {
          playBtn.click();
          return;
        }

        // Fallback: open modal directly
        const liteYt = document.querySelector(
          `.video-modal__container lite-youtube[videoid="${videoId}"]`
        );
        if (!liteYt) return;

        const modal = liteYt.closest(".video-modal__container");
        if (modal) {
          modal.classList.add("active");
          liteYt.click();
        }
      });
    });
  }

  applyVariant(variant) {
    this.currentVariant = variant;
    this.updateGallery(variant);
    this.updateThumbs(variant);
    this.updateURL(variant);
    this.dispatchEvent(
      new CustomEvent("main-product:variant-change", {
        detail: { variant },
        bubbles: true,
      })
    );
  }

  updateGallery(variant) {
    if (!this.variantGalleries || !this.variantGalleries.length) return;

    const targetId = String(variant.id);

    this.variantGalleries.forEach((gallery) => {
      const isActive = String(gallery.dataset.variantImages) === targetId;

      if (isActive) {
        gallery.classList.remove("hidden", "absolute", "inset-0");
        gallery.classList.add("relative");
      } else {
        gallery.classList.add("hidden", "absolute", "inset-0");
        gallery.classList.remove("relative");
      }
    });
  }

  updateThumbs(variant) {
    if (!variant) return;

    document.querySelectorAll("[data-variant-thumbs]").forEach((thumb) => {
      thumb.classList.add("lg:hidden");
    });

    if (window.innerWidth > 1024) {
      const activeThumbs = document.querySelector(
        `[data-variant-thumbs="${variant.id}"]`
      );
      if (activeThumbs) activeThumbs.classList.remove("lg:hidden");
    }
  }

  updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({ path: url.toString() }, "", url.toString());
  }

  handleAutoAction() {
    const url = new URL(window.location.href);
    if (url.searchParams.get("auto") !== "true") return;

    url.searchParams.delete("auto");
    history.replaceState({}, "", url.toString());

    // We need two things before clicking:
    // 1. TeddyModal's click handlers must be registered (happens on DOMContentLoaded)
    // 2. Layout must be computed (so offsetParent visibility check works)
    //
    // DOMContentLoaded fires after all deferred scripts. TeddyModal registers its
    // handlers inside a DOMContentLoaded callback. Our connectedCallback fires during
    // deferred script execution, so we may register our DOMContentLoaded listener
    // BEFORE TeddyModal's. Using setTimeout(0) inside DOMContentLoaded ensures we
    // run after all synchronous DOMContentLoaded handlers. Then requestAnimationFrame
    // ensures layout is computed for the offsetParent check.
    const clickWhenReady = () => {
      setTimeout(() => {
        requestAnimationFrame(() => this._clickVisibleCta());
      }, 0);
    };

    if (document.readyState === "complete") {
      // DOMContentLoaded already fired — TeddyModal handlers are registered.
      clickWhenReady();
    } else {
      // readyState is "loading" or "interactive" — deferred scripts run at "interactive"
      // but DOMContentLoaded hasn't fired yet, so TeddyModal handlers aren't registered.
      document.addEventListener("DOMContentLoaded", clickWhenReady, { once: true });
    }
  }

  _clickVisibleCta() {
    const selectors = [
      "#add-to-bag",
      ".notify-me-btn",
      "[data-csm-trigger]",
      "[data-cs-trigger]",
    ];

    for (const sel of selectors) {
      const btn = this.querySelector(sel);
      if (btn && btn.offsetParent !== null) {
        btn.click();
        return;
      }
    }
  }

}

if (!customElements.get("main-product")) {
  customElements.define("main-product", MainProduct);
}
